import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  GlobalStyles,
  Alert,
} from "@mui/material";
import {
  ThemeProvider,
  createTheme,
  type ThemeOptions,
} from "@mui/material/styles";
import { themeOptions } from "../../theme/buildTheme";
import type { Mode, ThemeDraft } from "../../theme/buildTheme";
import {
  CheckCircleOutlineIcon,
  ErrorOutlineIcon,
  TimerOffIcon,
} from "../../icons";
import { ExternalValidationForm } from "./ExternalValidationForm";
import { externalValidationPages } from "./externalValidationPages";
import type {
  ExternalValidationInfo,
  ExternalValidationLanguage,
  ExternalValidationSubmitResult,
  ExternalValidationTenantInfo,
} from "./externalValidationTypes";

// Las props de una pantalla propia del proyecto (src/common/pages/externalValidationPages.tsx).
export type { ExternalValidationPageProps } from "./externalValidationTypes";

// El idioma de la pantalla propia: el del formulario genérico
// (REACT_APP_DEFAULT_LANGUAGE) o, si no es uno de los tres, el del proyecto.
const ENV_LANGUAGE = process.env.REACT_APP_DEFAULT_LANGUAGE;
const LANGUAGE: ExternalValidationLanguage =
  ENV_LANGUAGE === "es" || ENV_LANGUAGE === "en" || ENV_LANGUAGE === "pt"
    ? ENV_LANGUAGE
    : "es";

// La pantalla propia del proyecto para una entidad, si la registró en
// externalValidationPages. Solo cuentan las claves propias del asiento: una
// heredada (constructor, toString) no es una pantalla.
const projectPageFor = (entityName: string) =>
  Object.prototype.hasOwnProperty.call(externalValidationPages, entityName)
    ? externalValidationPages[entityName]
    : undefined;

const BASE_URL =
  process.env.REACT_APP_API_URL?.replace("/graphql", "") ||
  "http://localhost:4000";

/**
 * El marco de la página sin tarjeta (theme.brand.publicCard: false en hkconfig.json):
 * el fondo de marca, el modo del tema del marco, lo que se fusiona sobre ese modo y
 * el gris de lo inactivo. Lo derivó y lo midió el generador contra el fondo (pares
 * publicFrame.* de la comprobación de contraste). null: la tarjeta de siempre.
 */
type PublicFrame = {
  background: string;
  mode: Mode;
  set: NonNullable<ThemeDraft["dark"]>;
  actionDisabled: string;
};
export const PUBLIC_FRAME: PublicFrame | null = {
  background: "#17405C",
  mode: "dark",
  set: {
    page: {
      bg: "#17405C",
      on: "#FFFFFF",
      onMuted: "#C5CFD6",
      border: "#5D798D",
      borderStrong: "#7F96A5",
      hover: "#2A4F69",
    },
    raised: {
      bg: "#17405C",
      on: "#FFFFFF",
      onMuted: "#C5CFD6",
      border: "#748C9D",
      borderStrong: "#7F96A5",
      hover: "#2A4F69",
    },
    sunken: {
      bg: "#254B66",
      on: "#FFFFFF",
      onMuted: "#C5CFD6",
      border: "#5D798D",
      borderStrong: "#7F96A5",
      hover: "#3C5F76",
    },
    primary: {
      bg: "#A0BDDB",
      on: "#17405C",
      onMuted: "#17405C",
      border: "#A0BDDB",
      borderStrong: "#A0BDDB",
      hover: "#AEC7E0",
      subtle: "#325975",
      onSubtle: "#FFFFFF",
    },
    secondary: {
      bg: "#B5B9C0",
      on: "#17405C",
      onMuted: "#17405C",
      border: "#B5B9C0",
      borderStrong: "#B5B9C0",
      hover: "#C0C3C9",
      subtle: "#375870",
      onSubtle: "#FFFFFF",
    },
    error: {
      bg: "#EFA4AB",
      on: "#17405C",
      onMuted: "#17405C",
      border: "#EFA4AB",
      borderStrong: "#EFA4AB",
      hover: "#F1B2B8",
      subtle: "#42546C",
      onSubtle: "#FFFFFF",
    },
    warning: {
      bg: "#EDA757",
      on: "#17405C",
      onMuted: "#17405C",
      border: "#EDA757",
      borderStrong: "#EDA757",
      hover: "#F0B470",
      subtle: "#42555B",
      onSubtle: "#FFFFFF",
    },
    success: {
      bg: "#82C58F",
      on: "#17405C",
      onMuted: "#17405C",
      border: "#82C58F",
      borderStrong: "#82C58F",
      hover: "#95CEA0",
      subtle: "#2C5B66",
      onSubtle: "#FFFFFF",
    },
    info: {
      bg: "#A0BDDB",
      on: "#17405C",
      onMuted: "#17405C",
      border: "#A0BDDB",
      borderStrong: "#A0BDDB",
      hover: "#AEC7E0",
      subtle: "#325975",
      onSubtle: "#FFFFFF",
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#C5CFD6",
      disabled: "#8BA0AE",
    },
    divider: "#5D798D",
    focusRing: "#FFFFFF",
    families: {
      field: {
        border: "#8BA0AE",
        borderHover: "#FFFFFF",
        borderFocus: "#A0BDDB",
      },
    },
  },
  actionDisabled: "#748C9D",
};

// El tema del marco: el de la aplicación en el modo del marco, con el conjunto del
// marco encima (como un borrador del banco de diseño) y el gris de lo inactivo.
// Paper sin el velo claro que MUI le pone a lo elevado en modo oscuro (diálogos,
// menús): el fondo que se ve es el que se midió. Las opciones van como ThemeOptions:
// las variantes propias de MuiButton no cumplen el tipo de las variables CSS de MUI
// (buildTheme lo esquiva igual, con un @ts-ignore).
const createPublicFrameTheme = (frame: PublicFrame) => {
  const options = themeOptions(
    frame.mode,
    frame.mode === "dark" ? { dark: frame.set } : { light: frame.set }
  );
  return createTheme(
    {
      ...options,
      palette: {
        ...options.palette,
        action: { ...options.palette.action, disabled: frame.actionDisabled },
      },
    } as ThemeOptions,
    {
      components: {
        MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      },
    }
  );
};

// Con el marco declarado, PUBLIC_FRAME nunca es null.
const publicFrameTheme = createPublicFrameTheme(PUBLIC_FRAME!);

type VerifyState =
  | "loading_info"
  | "ready"
  | "loading"
  | "verified"
  | "already_verified"
  | "expired"
  | "error"
  | "data_validation"
  | "submitted";

type TenantInfo = ExternalValidationTenantInfo;

type DataValidationInfo = ExternalValidationInfo;

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const vid = searchParams.get("vid");
  const sig = searchParams.get("sig");
  const [state, setState] = useState<VerifyState>(
    !vid || !sig ? "error" : "loading_info"
  );
  const [tenantInfo, setTenantInfo] = useState<TenantInfo>({});
  const [errorMessage, setErrorMessage] = useState("");
  const [dataValidation, setDataValidation] =
    useState<DataValidationInfo | null>(null);
  // null: la pantalla propia confirmó sin decir cuántos cambios hubo.
  const [submittedChanges, setSubmittedChanges] = useState<number | null>(0);
  // La confirmación que escribió la pantalla propia, en su idioma; null: la de la página.
  const [submittedConfirmation, setSubmittedConfirmation] =
    useState<NonNullable<
      ExternalValidationSubmitResult["confirmation"]
    > | null>(null);

  // Al confirmar, la pantalla propia del proyecto deja el foco en un botón que
  // desaparece: al llegar a la confirmación el foco va a su título, para que un
  // lector de pantalla la lea en vez de quedarse en el body.
  const submittedTitleRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (state === "submitted") submittedTitleRef.current?.focus();
  }, [state]);

  useEffect(() => {
    if (!vid || !sig) return;

    const fetchInfo = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/public/verify-email/info?vid=${encodeURIComponent(
            vid
          )}&sig=${encodeURIComponent(sig)}`
        );
        const data = await response.json();

        if (!data.success) {
          if (data.error === "expired") {
            setState("expired");
          } else {
            setState("error");
            setErrorMessage(data.error || "unknown_error");
          }
          return;
        }

        if (data.tenantInfo) {
          setTenantInfo(data.tenantInfo);
        }

        if (data.mode === "already_verified") {
          setState("already_verified");
        } else if (data.mode === "dataValidation") {
          setDataValidation({
            entityName: data.entityName,
            entityData: data.entityData,
            fields: data.fields,
            groups: data.groups,
            editableFields: data.editableFields,
            lookups: data.lookups,
          });
          setState("data_validation");
        } else {
          setState("ready");
        }
      } catch {
        setState("error");
        setErrorMessage("network_error");
      }
    };

    fetchInfo();
  }, [vid, sig]);

  const handleVerify = async () => {
    if (!vid || !sig) return;
    setState("loading");

    try {
      const response = await fetch(`${BASE_URL}/api/public/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vid, sig }),
      });

      const data = await response.json();

      if (data.success) {
        setTenantInfo((prev) => ({
          ...prev,
          tenantName: data.tenantName || prev.tenantName,
          tenantLogo: data.tenantLogo || prev.tenantLogo,
          tenantPublicUrl: data.tenantPublicUrl || prev.tenantPublicUrl,
        }));
        setState(
          data.message === "already_verified" ? "already_verified" : "verified"
        );
      } else {
        if (data.error === "expired") {
          setState("expired");
        } else {
          setState("error");
          setErrorMessage(data.error || "unknown_error");
        }
      }
    } catch {
      setState("error");
      setErrorMessage("network_error");
    }
  };

  const handleDataValidationSubmitted = (
    result?: ExternalValidationSubmitResult
  ) => {
    setSubmittedChanges(
      typeof result?.changes === "number" ? result.changes : null
    );
    setSubmittedConfirmation(result?.confirmation ?? null);
    if (result?.tenantInfo) {
      setTenantInfo((prev) => ({ ...prev, ...result.tenantInfo }));
    }
    setState("submitted");
  };

  const renderContent = () => {
    switch (state) {
      case "loading_info":
        return (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Cargando...
            </Typography>
          </Box>
        );

      case "ready":
        return (
          <>
            <Typography variant="h5" gutterBottom>
              Verificación de email
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Hacé clic en el botón para confirmar tu dirección de email.
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleVerify}
              sx={{ px: 4, py: 1.5 }}
            >
              Confirmar verificación
            </Button>
          </>
        );

      case "loading":
        return (
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Verificando...
            </Typography>
          </Box>
        );

      case "data_validation": {
        if (!dataValidation) return null;
        // La pantalla propia del proyecto para esta entidad; si no registró
        // ninguna, el formulario genérico.
        const ProjectPage = projectPageFor(dataValidation.entityName);
        if (ProjectPage) {
          return (
            <ProjectPage
              vid={vid!}
              sig={sig!}
              info={dataValidation}
              tenantInfo={tenantInfo}
              language={LANGUAGE}
              onSubmitted={handleDataValidationSubmitted}
            />
          );
        }
        return (
          <ExternalValidationForm
            entityData={dataValidation.entityData}
            fields={dataValidation.fields}
            groups={dataValidation.groups}
            editableFields={dataValidation.editableFields}
            lookups={dataValidation.lookups}
            vid={vid!}
            sig={sig!}
            tenantInfo={tenantInfo}
            onSubmitted={handleDataValidationSubmitted}
          />
        );
      }

      case "submitted":
        return (
          <>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 64, color: "success.main", mb: 2 }}
            />
            <Typography
              variant="h5"
              gutterBottom
              color="success.main"
              ref={submittedTitleRef}
              tabIndex={-1}
              sx={{ outline: "none" }}
            >
              {submittedConfirmation
                ? submittedConfirmation.title
                : "Datos confirmados"}
            </Typography>
            {submittedConfirmation ? (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {submittedConfirmation.message}
              </Typography>
            ) : (
              <>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {tenantInfo.tenantName
                    ? `Tu email fue verificado para ${tenantInfo.tenantName}.`
                    : "Tu email fue verificado y tus datos fueron actualizados."}
                </Typography>
                {submittedChanges !== null && (
                  <Typography variant="body2" color="text.secondary">
                    {submittedChanges > 0
                      ? `${submittedChanges} cambio(s) aplicado(s).`
                      : "No se realizaron cambios en tus datos."}
                  </Typography>
                )}
              </>
            )}
            {tenantInfo.tenantPublicUrl && (
              <Button
                variant="outlined"
                href={tenantInfo.tenantPublicUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mt: 2 }}
              >
                {tenantInfo.tenantName || "Ir al sitio"}
              </Button>
            )}
          </>
        );

      case "verified":
        return (
          <>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 64, color: "success.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom color="success.main">
              Email verificado
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {tenantInfo.tenantName
                ? `Tu email fue verificado para ${tenantInfo.tenantName}.`
                : "Tu email fue verificado exitosamente."}
            </Typography>
            {tenantInfo.tenantPublicUrl && (
              <Button
                variant="outlined"
                href={tenantInfo.tenantPublicUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mt: 1 }}
              >
                {tenantInfo.tenantName || "Ir al sitio"}
              </Button>
            )}
          </>
        );

      case "already_verified":
        return (
          <>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 64, color: "info.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom color="info.main">
              Ya verificado
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Este email ya fue verificado anteriormente.
            </Typography>
          </>
        );

      case "expired":
        return (
          <>
            <TimerOffIcon sx={{ fontSize: 64, color: "warning.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom color="warning.main">
              Enlace expirado
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Este enlace de verificación expiró. Contactá al administrador para
              solicitar uno nuevo.
            </Typography>
          </>
        );

      case "error":
        return (
          <>
            <ErrorOutlineIcon
              sx={{ fontSize: 64, color: "error.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom color="error.main">
              Error de verificación
            </Typography>
            <Alert severity="error" sx={{ mt: 1 }}>
              {errorMessage === "invalid_signature"
                ? "El enlace de verificación no es válido."
                : errorMessage === "network_error"
                ? "Error de conexión. Volvé a intentar."
                : errorMessage === "scope_changed" ||
                  errorMessage === "data_validation_required"
                ? "Este enlace cambió desde que abriste la página. Abrí el último correo que te llegó."
                : "No se pudo verificar el email."}
            </Alert>
          </>
        );
    }
  };

  // El logo de la empresa: el server manda el archivo (Tenant.logo_data: nombre,
  // tipo, tamaño y url) como un objeto, no como una URL, y solo un texto sirve de
  // src. Sin un logo usable no se dibuja la imagen.
  const tenantLogo =
    typeof tenantInfo.tenantLogo === "string" && tenantInfo.tenantLogo !== ""
      ? tenantInfo.tenantLogo
      : null;
  const showsLogo = state !== "loading_info" && state !== "loading";

  const content = (
    <>
      {showsLogo && tenantLogo && (
        <Box sx={{ mb: 3 }}>
          <img
            src={tenantLogo}
            alt={tenantInfo.tenantName || ""}
            style={{ maxHeight: 48, maxWidth: 180 }}
          />
        </Box>
      )}
      {renderContent()}
    </>
  );

  return (
    <ThemeProvider theme={publicFrameTheme}>
      <GlobalStyles
        styles={(theme) => ({
          body: { backgroundColor: theme.palette.background.default },
        })}
      />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
          color: "text.primary",
          p: 2,
        }}
      >
        <Box
          sx={{
            py: 4,
            maxWidth: state === "data_validation" ? 640 : 480,
            width: "100%",
            textAlign: "center",
          }}
        >
          {content}
        </Box>
      </Box>
    </ThemeProvider>
  );
};
